import React from "react";
import { observer } from "mobx-react-lite";
import {
  Box,
  IconButton,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
  Skeleton,
  CircularProgress,
} from "@mui/material";
import { School as SchoolIcon } from "@mui/icons-material";

interface School {
  id: number;
  name: string;
}

interface SchoolSelectorProps {
  schools: School[];
  selectedSchoolId: string | null;
  onSchoolChange: (schoolId: string | null) => void;
  expanded: boolean;
  onToggle?: () => void;
  isRtl: boolean;
  t: (key: string) => string;
  showAllSchoolsOption?: boolean;
  isLoading?: boolean;
}

const SchoolSelector: React.FC<SchoolSelectorProps> = ({
  schools,
  selectedSchoolId,
  onSchoolChange,
  expanded,
  onToggle,
  isRtl,
  t,
  showAllSchoolsOption = false,
  isLoading = false,
}) => {
  const selectValue = selectedSchoolId !== null 
    ? selectedSchoolId 
    : (showAllSchoolsOption ? "" : schools[0]?.id || "");

  return (
    <Box sx={{ p: 1.5, borderBottom: "1px solid rgba(255,255,255,0.2)" }}>
      {expanded ? (
        <Box>
          <Typography variant="body2" sx={{ mb: 0.5, opacity: 0.8 }}>
            {t("school")}:
          </Typography>
          {isLoading ? (
              <Skeleton
                variant="text"
                width={216}
                height={42}
                sx={{ bgcolor: "rgba(255,255,255,0.1)" }}
              />
          ) : schools.length === 0 ? (
            <Typography
              variant="body1"
              sx={{ fontWeight: "bold", fontSize: "1rem", opacity: 0.7 }}
            >
              {t("noSchoolsAvailable") || "No schools available"}
            </Typography>
          ) : schools.length === 1 ? (
            <Typography
              variant="body1"
              sx={{ fontWeight: "bold", fontSize: "1rem" }}
            >
              {schools[0].name}
            </Typography>
          ) : (
            <FormControl fullWidth size="small">
              <Select
                value={selectValue}
                inputProps={{ 'aria-label': 'בחירת בית ספר' }}
                onChange={(e) => {
                  const value = e.target.value;
                  onSchoolChange(value === "" ? null : String(value));
                }}
                displayEmpty
                IconComponent={() => null}
                renderValue={(selected) => {
                  if (!selected) {
                    return showAllSchoolsOption ? t("allSchools") : schools[0]?.name || "";
                  }
                  const selectedSchool = schools.find(school => String(school.id) === String(selected));
                  return selectedSchool?.name || t("selectedSchool");
                }}
                sx={{
                  color: "white",
                  backgroundColor: "rgba(255,255,255,0.1)",
                  "& .MuiSelect-select": {
                    fontWeight: "bold",
                    fontSize: "1rem",
                    padding: "8px 12px",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    border: "1px solid rgba(255,255,255,0.5)",
                  },
                  "& .MuiSelect-icon": {
                    display: "none",
                  },
                }}
              >
                {showAllSchoolsOption && (
                  <MenuItem
                    key="all-schools"
                    value=""
                    sx={{ fontWeight: "bold", fontStyle: "italic" }}
                  >
                    {t("allSchools")}
                  </MenuItem>
                )}
                {schools.map((school) => (
                  <MenuItem
                    key={school.id}
                    value={String(school.id)}
                    sx={{ fontWeight: "bold" }}
                  >
                    {school.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      ) : (
        <Tooltip title={t("school")} placement={isRtl ? "left" : "right"}>
          <IconButton
            onClick={onToggle}
            disabled={isLoading}
            sx={{
              color: "white",
              backgroundColor: "rgba(255,255,255,0.1)",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
              "&:disabled": { opacity: 0.6 },
            }}
          >
            {isLoading ? (
              <CircularProgress size={20} sx={{ color: "white" }} />
            ) : (
              <SchoolIcon />
            )}
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default observer(SchoolSelector);